import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/entreprise/Chauffeur-pour-entreprise.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const biotEn: ChauffeurPage = {
  slug: "biot",
  title: "VTC & private chauffeur in Biot",
  subtitle:
    "Private chauffeur in Biot: Nice airport transfers, the hilltop village, the glassworks and the Marineland area.",
  keywords: ["vtc biot", "private chauffeur biot", "biot glassworks chauffeur"],
  hero: {
    title: "VTC & private chauffeur in Biot",
    subtitle:
      "Nice airport transfers, the hilltop village and glassworks, the Marineland area and Biot station — 24/7.",
    keywords: ["VTC Biot", "Biot village chauffeur", "Nice airport transfer"],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Biot, village and glassworks",
    badges: ["Village & glassworks", "Flight tracking", "Bilingual chauffeurs"],
  },
  benefits: {
    heading: "Why a chauffeur in Biot",
    items: [
      {
        title: "Hilltop village & craftsmen",
        description:
          "The Biot bubbled-glass glassworks, potters and galleries: drop-off at the foot of the medieval village, with no parking worry.",
      },
      {
        title: "Marineland area & seafront",
        description:
          "Biot station, beaches and the shoreline: easy drop-off and pick-up on the sea side, away from the lanes.",
      },
      {
        title: "Nice airport in ~20 min",
        description:
          "A direct A8 link with flight tracking and a personal welcome, both ways.",
      },
      {
        title: "Local roots",
        description:
          "Based on the French Riviera, rated 5.0/5 from 38 Google reviews, with bilingual FR/EN chauffeurs.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Biot",
    services: [
      {
        title: "Nice airport transfers",
        text: "Biot ⇄ Nice Côte d'Azur airport in about 20 minutes via the A8, with flight tracking and a terminal welcome.",
      },
      {
        title: "Village & glassworks",
        text: "Drop-off at the foot of the hilltop village, waiting during the glassworks and gallery visit, pick-up with no constraint.",
      },
      {
        title: "Fernand Léger National Museum",
        text: "A link to the museum and its gardens, with waiting on site or a return to your hotel.",
      },
      {
        title: "Marineland area & beaches",
        text: "Service to the Marineland area, Biot station and the beaches, handy for families.",
      },
      {
        title: "Sophia Antipolis, Biot side",
        text: "Executives met and business appointments on the Biot side of the tech park, with company billing.",
      },
    ],
  },
  locations: {
    heading: "Areas & pick-up points",
    items: [
      {
        name: "Village of Biot",
        description:
          "Medieval hilltop village, Place des Arcades and pedestrian lanes.",
      },
      {
        name: "Biot glassworks",
        description: "Bubbled-glass workshop and craft boutiques.",
      },
      {
        name: "Fernand Léger National Museum",
        description:
          "Museum and gardens devoted to the painter, below the village.",
      },
      {
        name: "Biot station (Marineland area)",
        description: "Seafront, beaches and the shoreline hub.",
      },
      {
        name: "Sophia Antipolis (Biot side)",
        description: "Schools, companies and business parks of the tech park.",
      },
      { name: "Nice airport", description: "~20 min via the A8." },
    ],
  },
  generic: {
    heading: "Biot, the village of glass between hills and shore",
    subheading:
      "From the craftsmen's hilltop village to the seafront Marineland area, one chauffeur for the whole commune.",
    paragraphs: [
      "Biot is first a medieval hilltop village, famous for its bubbled-glass glassworks, its potters and its galleries clustered around Place des Arcades. The lanes are pedestrian and parking is scarce: the chauffeur drops you at the foot of the village and collects you on request.",
      "Below, the commune runs down to the shore: Biot station, the Marineland area and the beaches form a lively hub, a few minutes from the village by road. We link the two easily, so you never hunt for a space on the sea side.",
      "Biot also hosts part of Sophia Antipolis, with schools and companies: for these business trips we handle the welcome at Nice airport — about 20 minutes away via the A8 — and the appointments, with company billing. The Fernand Léger National Museum rounds off the cultural visit.",
    ],
    bullets: [
      "Drop-off at the village foot",
      "Marineland area & beaches",
      "Glassworks, galleries & Léger Museum",
    ],
  },
  process: {
    heading: "Book a chauffeur in Biot",
    steps: [
      "Share your route or your addresses (airport, village, glassworks, Marineland area).",
      "We confirm the right vehicle and the fixed fare or hourly hire.",
      "Your chauffeur tracks your flight and greets you on time.",
      "Drop-off at the foot of the village or on the sea side, pick-up on request.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Biot"),
    {
      q: "How long is the drive to Nice airport from Biot?",
      a: "Allow about 20 minutes via the A8 outside peak hours. We track your flight to adjust the pick-up time, both ways.",
    },
    {
      q: "Do you serve the Marineland area and the beaches with children?",
      a: "Yes. We drop off and collect in the Marineland area, at Biot station and along the beaches, with child seats on request — handy for family outings with no parking worry.",
    },
    {
      q: "How far in advance should I book?",
      a: "Book as early as possible in high season and on busy weekends; we also handle last-minute requests subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English, used to an international clientele.",
    },
    {
      q: "Which vehicles do you offer in Biot?",
      a: "Mercedes E-Class and S-Class sedans, a 7-seat van, SUV and VIP minibus, depending on the number of passengers and the occasion.",
    },
    {
      q: "Can you reach the village of Biot by car?",
      a: "The heart of the hilltop village is pedestrian. The chauffeur drops you as close as possible, at the foot of the village, then collects you at the same spot after the glassworks and gallery visit.",
    },
  ],
  relatedLinks: [
    {
      label: "Sophia Antipolis chauffeur rates",
      href: "/en/chauffeur/sophia-antipolis",
    },
    { label: "Nice Airport rates and routes", href: "/en/chauffeur/airport" },
    { label: "Airport transfers in Nice", href: "/en/chauffeur/nice" },
    {
      label: "Chauffeur hire on the Riviera",
      href: "/en/chauffeur/french-riviera",
    },
  ],
  cta: {
    title: "Your private chauffeur in Biot",
    text: "Village, glassworks, Marineland area or airport transfer — quick reply, 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Private chauffeur in Biot 24/7 — car service & transfers",
    description:
      "Private car service in Biot: Nice airport transfer (~20 min), the hilltop village, the glassworks and the Marineland area. 5.0/5 from 38 reviews.",
    canonical: "/en/chauffeur/biot",
  },
};

export default biotEn;
